import dayjs from "dayjs";
import {
  AlertCircleIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  ExternalLinkIcon,
  RefreshCwIcon,
  TriangleAlertIcon,
  WrapTextIcon,
} from "lucide-react";
import React from "react";

import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DataList,
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover } from "@/components/ui/popover";
import { Sheet } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { cn } from "@/components/ui/utils";

type Status = "PENDING" | "SUCCESS" | "FAILED";

export function IntegrationMessageSheet({
  messageId,
  onClose,
}: {
  messageId: number | null;
  onClose: () => void;
}) {
  // Internal "viewing" id so the sheet can navigate between attempts in the
  // same retry chain without forcing the parent to track it. We seed from the
  // prop and keep them in sync when the parent opens a new message.
  const [viewingId, setViewingId] = React.useState<number | null>(messageId);

  React.useEffect(() => {
    setViewingId(messageId);
  }, [messageId]);

  const isOpen = viewingId != null;

  return (
    <Sheet
      isOpen={isOpen}
      onOpenChange={(flag) => {
        if (!flag) {
          setViewingId(null);
          onClose();
        }
      }}
      side="right"
      classNames={{
        content: "left-2 w-auto sm:left-auto sm:right-2 sm:max-w-4xl",
      }}
    >
      <Dialog>
        <DialogHeader className="border-b">
          <Text
            size="1"
            muted
            className="font-medium uppercase tracking-wide tabular-nums"
          >
            {viewingId != null ? `Message #${viewingId}` : "Message"}
          </Text>
          <DialogTitle>Integration message</DialogTitle>
        </DialogHeader>
        <DialogBody className="gap-6 py-5">
          {viewingId != null && (
            <MessageDetail id={viewingId} onNavigate={setViewingId} />
          )}
        </DialogBody>
      </Dialog>
    </Sheet>
  );
}

function MessageDetail({
  id,
  onNavigate,
}: {
  id: number;
  onNavigate: (id: number) => void;
}) {
  const utils = trpc.useUtils();
  // Tracks the last manual replay (retry OR resend) so the auto-poll below can
  // refetch briefly until the worker's new attempt row shows up as the
  // `nextMessageId` link, and the hero label can show "Last queued Xs ago".
  const [lastReplay, setLastReplay] = React.useState<{
    at: number;
    kind: "retry" | "resend";
  } | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } =
    trpc.integrations.getMessage.useQuery(
      { id },
      {
        // Auto-poll so users see things change without having to refresh:
        // - while this message itself is PENDING (the outcome will arrive)
        // - briefly after a manual retry/resend (the worker creates a NEW
        //   attempt row, so we need to refetch this row to surface its
        //   `nextMessageId` chain link).
        refetchInterval: (query) => {
          const current = query.state.data;
          if (current?.status === "PENDING") return 3000;
          if (
            lastReplay != null &&
            Date.now() - lastReplay.at < 30_000 &&
            current?.nextMessageId == null
          ) {
            return 2000;
          }
          return false;
        },
        refetchIntervalInBackground: false,
      },
    );

  const retry = trpc.integrations.retryMessage.useMutation({
    onSuccess: () => {
      toast.success("Retry queued");
      setLastReplay({ at: Date.now(), kind: "retry" });
      utils.integrations.listMessages.invalidate();
      utils.integrations.getMessage.invalidate({ id });
    },
    onError: (err) => {
      toast.error("Retry failed", { description: err.message });
    },
  });

  const resend = trpc.integrations.resendMessage.useMutation({
    onSuccess: () => {
      toast.success("Resend queued");
      setLastReplay({ at: Date.now(), kind: "resend" });
      utils.integrations.listMessages.invalidate();
      utils.integrations.getMessage.invalidate({ id });
    },
    onError: (err) => {
      toast.error("Resend failed", { description: err.message });
    },
  });

  if (isError) {
    return (
      <FetchErrorState
        message={error?.message ?? "Unable to load message"}
        onRetry={() => refetch()}
        isRetrying={isFetching}
      />
    );
  }

  if (isLoading || !data) return <DetailSkeleton />;

  const created = dayjs(data.createdAt);
  const completed = data.completedAt ? dayjs(data.completedAt) : null;
  const durationMs = completed ? completed.diff(created) : null;
  // Replay (retry/resend) is only offered on the latest attempt in a chain —
  // resending from an older message would fork the lineage and the server
  // rejects it anyway (CONFLICT). Direct admins to the newest attempt instead
  // by hiding the action when this message already has a successor.
  const isLatestInChain = data.nextMessageId == null;
  const hasReportId = !!data.reportId;
  const canRetry = data.status === "FAILED" && hasReportId && isLatestInChain;
  const canResend = data.status === "SUCCESS" && hasReportId && isLatestInChain;
  const orgLabel =
    data.org?.name ?? (data.orgId != null ? `#${data.orgId}` : null);
  const accessionLabel =
    data.accessionId != null ? String(data.accessionId) : null;
  const subjectLabel = data.subject?.caseId ?? null;

  return (
    <div className="flex flex-col gap-6">
      <StatusHero
        status={data.status}
        kind={data.kind}
        direction={data.direction}
        attempt={data.attempt}
        responseStatus={data.responseStatus}
        canRetry={canRetry}
        canResend={canResend}
        isReplaying={retry.isPending || resend.isPending}
        lastReplay={lastReplay}
        reportId={data.reportId}
        onConfirmRetry={() => retry.mutate({ id })}
        onConfirmResend={(reason) => resend.mutate({ id, reason })}
      />

      <TimingStrip
        createdAt={data.createdAt}
        completedAt={data.completedAt}
        durationMs={durationMs}
      />

      {data.errorMessage ? (
        data.status === "SUCCESS" ? (
          <WarningCallout message={data.errorMessage} />
        ) : (
          <ErrorCallout message={data.errorMessage} />
        )
      ) : null}

      <Section title="Details">
        <DataList labelWidth={120}>
          <DataListItem>
            <DataListLabel>Accession</DataListLabel>
            <DataListValue>
              {data.accessionId != null && accessionLabel ? (
                <Link
                  to="/accessions/$id"
                  params={{ id: String(data.accessionId) }}
                  className="inline-flex items-center gap-1"
                >
                  {accessionLabel}
                  <ExternalLinkIcon className="size-3" />
                </Link>
              ) : undefined}
            </DataListValue>
          </DataListItem>
          <DataListItem>
            <DataListLabel>Subject</DataListLabel>
            <DataListValue>
              {data.subject != null ? (
                <Link
                  to="/subjects/$id"
                  params={{ id: String(data.subject.id) }}
                  className="inline-flex items-center gap-1"
                >
                  {subjectLabel ?? `#${data.subject.id}`}
                  <ExternalLinkIcon className="size-3" />
                </Link>
              ) : undefined}
            </DataListValue>
          </DataListItem>
          <DataListItem>
            <DataListLabel>Report</DataListLabel>
            <DataListValue>
              {data.reportId != null ? `#${data.reportId}` : undefined}
            </DataListValue>
          </DataListItem>
          <DataListItem>
            <DataListLabel>Org</DataListLabel>
            <DataListValue>{orgLabel ?? undefined}</DataListValue>
          </DataListItem>
          <DataListItem>
            <DataListLabel>External ID</DataListLabel>
            <DataListValue copyable>
              {data.externalId ?? undefined}
            </DataListValue>
          </DataListItem>
          <DataListItem>
            <DataListLabel>Endpoint</DataListLabel>
            <DataListValue copyable className="min-w-0">
              {data.endpoint ? (
                <span className="font-mono text-xs wrap-break-word">
                  {data.endpoint}
                </span>
              ) : undefined}
            </DataListValue>
          </DataListItem>
          {(data.previousMessageId != null || data.nextMessageId != null) && (
            <DataListItem>
              <DataListLabel>Attempt chain</DataListLabel>
              <DataListValue>
                <div className="flex flex-wrap items-center gap-1">
                  {data.previousMessageId != null && (
                    <Button
                      variant="outline"
                      size="sm"
                      onPress={() =>
                        onNavigate(data.previousMessageId as number)
                      }
                      className="h-6 gap-1 px-2 text-xs"
                    >
                      <ChevronLeftIcon className="size-3" />#
                      {data.previousMessageId}
                    </Button>
                  )}
                  {data.nextMessageId != null && (
                    <Button
                      variant="outline"
                      size="sm"
                      onPress={() => onNavigate(data.nextMessageId as number)}
                      className="h-6 gap-1 px-2 text-xs"
                    >
                      #{data.nextMessageId}
                      <ChevronRightIcon className="size-3" />
                    </Button>
                  )}
                </div>
              </DataListValue>
            </DataListItem>
          )}
        </DataList>
      </Section>

      <PayloadSection title="Request" value={data.request} />
      <PayloadSection title="Response" value={data.response} />
    </div>
  );
}

function StatusHero({
  status,
  kind,
  direction,
  attempt,
  responseStatus,
  canRetry,
  canResend,
  isReplaying,
  lastReplay,
  reportId,
  onConfirmRetry,
  onConfirmResend,
}: {
  status: Status;
  kind: string;
  direction: string;
  attempt: number;
  responseStatus: number | null;
  canRetry: boolean;
  canResend: boolean;
  isReplaying: boolean;
  lastReplay: { at: number; kind: "retry" | "resend" } | null;
  reportId: number | null;
  onConfirmRetry: () => void;
  onConfirmResend: (reason: string) => void;
}) {
  const tone = STATUS_TONE[status];
  const replayAgo = useRelativeTime(lastReplay?.at ?? null);

  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-start">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span
          aria-hidden="true"
          className={cn(
            "mt-1.5 inline-flex size-2.5 shrink-0 rounded-full",
            tone.dot,
            status === "PENDING" && "animate-pulse",
          )}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Text size="3" className="font-semibold">
              {tone.title}
            </Text>
            {responseStatus != null && (
              <Badge
                color={status === "FAILED" ? "red" : "gray"}
                size="sm"
                variant="outline"
              >
                HTTP {responseStatus}
              </Badge>
            )}
            {attempt > 1 && (
              <Badge color="gray" size="sm" variant="outline">
                Attempt {attempt}
              </Badge>
            )}
          </div>
          <Text size="2" muted>
            {prettyKind(kind)} · {direction.toLowerCase()}
          </Text>
        </div>
      </div>
      {(canRetry || canResend) && (
        <div className="flex flex-col items-end gap-1">
          {canRetry && (
            <RetryAction
              isRetrying={isReplaying}
              reportId={reportId}
              onConfirmRetry={onConfirmRetry}
            />
          )}
          {canResend && (
            <ResendAction
              isResending={isReplaying}
              reportId={reportId}
              onConfirmResend={onConfirmResend}
            />
          )}
          {replayAgo && lastReplay && (
            <Text size="1" muted className="tabular-nums">
              Last {lastReplay.kind} queued {replayAgo}
            </Text>
          )}
        </div>
      )}
    </div>
  );
}

function RetryAction({
  isRetrying,
  reportId,
  onConfirmRetry,
}: {
  isRetrying: boolean;
  reportId: number | null;
  onConfirmRetry: () => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button variant="outline" size="sm" isPending={isRetrying}>
        <RefreshCwIcon /> Retry
      </Button>
      <Popover className="w-72 p-0" placement="bottom end">
        <Dialog hideCloseButton className="gap-3 p-3">
          <div className="flex items-start gap-2">
            <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex flex-col gap-1">
              <Text size="2" className="font-medium">
                Retry this send?
              </Text>
              <Text size="1" muted>
                A new attempt will be queued
                {reportId != null ? ` for report #${reportId}` : ""}. The
                previous attempt failed, so the vendor never accepted it.
              </Text>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onPress={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onPress={() => {
                setIsOpen(false);
                onConfirmRetry();
              }}
            >
              <RefreshCwIcon /> Confirm retry
            </Button>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

/**
 * Resend a previously **successful** message. Distinct from {@link RetryAction}
 * because the vendor already accepted the original — re-sending creates a new
 * vendor-side event for an already-acknowledged submission. We require a
 * non-empty reason so the audit log captures *why* (donor demographic
 * correction, org config retag, vendor data loss, etc.), and we keep the
 * tone redder/scarier than the retry confirmation to prevent accidental
 * duplicates.
 */
function ResendAction({
  isResending,
  reportId,
  onConfirmResend,
}: {
  isResending: boolean;
  reportId: number | null;
  onConfirmResend: (reason: string) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const reasonId = React.useId();

  const trimmed = reason.trim();
  const isValid = trimmed.length > 0 && trimmed.length <= 500;

  function close() {
    setIsOpen(false);
    setReason("");
  }

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(o) => (o ? setIsOpen(o) : close())}
    >
      <Button variant="outline" size="sm" isPending={isResending}>
        <RefreshCwIcon /> Resend
      </Button>
      <Popover className="w-80 p-0" placement="bottom end">
        <Dialog hideCloseButton className="gap-3 p-3">
          <div className="flex items-start gap-2">
            <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-red-600 dark:text-red-400" />
            <div className="flex flex-col gap-1">
              <Text size="2" className="font-medium">
                Resend this successful submission?
              </Text>
              <Text size="1" muted>
                The vendor already accepted
                {reportId != null ? ` report #${reportId}` : " this report"}.
                Use this when upstream data changed (donor info, org config,
                case-type tag) and you want the vendor's record updated.
              </Text>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 dark:border-amber-900/40 dark:bg-amber-950/20">
            <Text
              size="1"
              className="font-medium text-amber-900 dark:text-amber-200"
            >
              What gets refreshed
            </Text>
            <Text size="1" className="text-amber-900/80 dark:text-amber-200/80">
              The JSON payload is rebuilt from current data: donor info, org
              config, case-type flags, and result values.{" "}
              <strong>The attached PDF is not re-rendered</strong> — it's the
              file stored when the report was finalized. To refresh the PDF
              itself, generate a corrected report instead.
            </Text>
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor={reasonId}
              className="text-xs font-medium text-muted-foreground"
            >
              Reason
            </label>
            <textarea
              id={reasonId}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Donor caseId corrected; resending so vendor record matches"
              rows={3}
              maxLength={500}
              autoFocus
              className="rounded-md border bg-background px-2 py-1.5 text-xs outline-hidden ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
            />
            <Text size="1" muted className="tabular-nums">
              {trimmed.length}/500
            </Text>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onPress={close}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              isDisabled={!isValid}
              onPress={() => {
                if (!isValid) return;
                onConfirmResend(trimmed);
                close();
              }}
            >
              <RefreshCwIcon /> Confirm resend
            </Button>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

/**
 * Returns a `dayjs.fromNow()` string for `timestamp` that updates on a short
 * interval so labels like "Last queued just now" tick to "5 seconds ago"
 * without requiring a refresh. Returns `null` when no timestamp is set.
 */
function useRelativeTime(timestamp: number | null): string | null {
  const [, force] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    if (timestamp == null) return;
    const interval = setInterval(force, 5000);
    return () => clearInterval(interval);
  }, [timestamp]);

  if (timestamp == null) return null;
  return dayjs(timestamp).fromNow();
}

function TimingStrip({
  createdAt,
  completedAt,
  durationMs,
}: {
  createdAt: string | Date;
  completedAt: string | Date | null;
  durationMs: number | null;
}) {
  return (
    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border/60 sm:grid-cols-3">
      <TimingCell
        label="Created"
        primary={dayjs(createdAt).format("YYYY-MM-DD HH:mm:ss")}
        secondary={dayjs(createdAt).fromNow()}
      />
      <TimingCell
        label="Completed"
        primary={
          completedAt ? dayjs(completedAt).format("YYYY-MM-DD HH:mm:ss") : "—"
        }
        secondary={completedAt ? dayjs(completedAt).fromNow() : "Pending"}
      />
      <TimingCell
        label="Duration"
        primary={durationMs != null ? formatDuration(durationMs) : "—"}
        secondary={durationMs != null ? "End-to-end" : "Awaiting response"}
      />
    </div>
  );
}

function TimingCell({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 bg-card px-3 py-2.5">
      <Text size="1" muted className="font-medium uppercase tracking-wide">
        {label}
      </Text>
      <Text size="2" className="font-medium tabular-nums">
        {primary}
      </Text>
      <Text size="1" muted className="tabular-nums">
        {secondary}
      </Text>
    </div>
  );
}

function ErrorCallout({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/30">
      <AlertCircleIcon className="mt-0.5 size-4 shrink-0 text-red-600 dark:text-red-400" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Text
          size="1"
          className="font-medium uppercase tracking-wide text-red-700 dark:text-red-300"
        >
          Error
        </Text>
        <pre className="overflow-auto whitespace-pre-wrap wrap-break-word text-xs text-red-800 dark:text-red-200">
          {message}
        </pre>
      </div>
    </div>
  );
}

function WarningCallout({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
      <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Text
          size="1"
          className="font-medium uppercase tracking-wide text-amber-700 dark:text-amber-300"
        >
          Warning
        </Text>
        <pre className="overflow-auto whitespace-pre-wrap wrap-break-word text-xs text-amber-800 dark:text-amber-200">
          {message}
        </pre>
      </div>
    </div>
  );
}

function Section({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Text
          size="1"
          className="font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {title}
        </Text>
        {actions}
      </div>
      {children}
    </section>
  );
}

function PayloadSection({ title, value }: { title: string; value: unknown }) {
  const [wrap, setWrap] = React.useState(true);
  const [copied, setCopied] = React.useState(false);

  const body = React.useMemo(() => formatJson(value), [value]);
  const isEmpty = value == null;
  const sizeLabel = isEmpty ? null : formatBytes(byteLength(body));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Section
      title={title}
      actions={
        !isEmpty && (
          <div className="flex items-center gap-1">
            {sizeLabel && (
              <Text size="1" muted className="tabular-nums">
                {sizeLabel}
              </Text>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={wrap ? "Disable wrap" : "Enable wrap"}
              onPress={() => setWrap((w) => !w)}
              className={cn(
                "text-muted-foreground hover:text-foreground",
                wrap && "text-foreground",
              )}
            >
              <WrapTextIcon />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Copy"
              onPress={handleCopy}
              className="text-muted-foreground hover:text-foreground"
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </Button>
          </div>
        )
      }
    >
      {isEmpty ? (
        <div className="rounded-md border border-dashed bg-muted/40 px-3 py-4 text-center">
          <Text size="2" muted>
            No payload
          </Text>
        </div>
      ) : (
        <pre
          className={cn(
            "max-h-96 overflow-auto rounded-md border bg-muted/40 p-3 text-xs",
            wrap ? "whitespace-pre-wrap wrap-break-word" : "whitespace-pre",
          )}
        >
          {body}
        </pre>
      )}
    </Section>
  );
}

function FetchErrorState({
  message,
  onRetry,
  isRetrying,
}: {
  message: string;
  onRetry: () => void;
  isRetrying: boolean;
}) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-950/20">
      <div className="flex items-start gap-3">
        <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-400" />
        <div className="flex flex-col gap-1">
          <Text size="2" className="font-medium text-red-800 dark:text-red-200">
            Couldn't load this message
          </Text>
          <Text size="1" muted>
            {message}
          </Text>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        isPending={isRetrying}
        onPress={onRetry}
      >
        <RefreshCwIcon /> Try again
      </Button>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-20 rounded-lg" />
      <Skeleton className="h-20 rounded-lg" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-32 rounded-md" />
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-48 rounded-md" />
      </div>
    </div>
  );
}

const STATUS_TONE = {
  SUCCESS: {
    title: "Sent",
    dot: "bg-green-500 dark:bg-green-400",
  },
  FAILED: {
    title: "Failed",
    dot: "bg-red-500 dark:bg-red-400",
  },
  PENDING: {
    title: "Pending",
    dot: "bg-amber-500 dark:bg-amber-400",
  },
} as const satisfies Record<Status, { title: string; dot: string }>;

function prettyKind(kind: string) {
  switch (kind) {
    case "ITRANSPLANT":
      return "iTransplant";
    default:
      return kind.charAt(0) + kind.slice(1).toLowerCase();
  }
}

function formatJson(value: unknown): string {
  if (value == null) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function byteLength(s: string): number {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(s).length;
  }
  return s.length;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 2 : 1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds - mins * 60);
  return `${mins}m ${secs}s`;
}
